// 8ErSAlH4 — My Studio: per-order delivered-photo download.
// For each rendered order row, checks the studio-backend job status
// (GET /job-status?orderId=, Firebase ID-token gated). When an order is
// DELIVERED, adds a "Download photo" button that fetches a short-lived,
// owner-scoped signed URL (GET /job-status/download?orderId=) and opens it.
// The customer-uploads bucket has uniform access (no public objects), so the
// signed URL is the ONLY working download path — a bare GCS URL 403s.
// Project-agnostic: reuses the firebase-auth.js app via getApp(), like
// studio-uploads.js, so it works before/after the studio-prod cutover.
import { getApp, getApps } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-app.js';
import { getAuth, onAuthStateChanged } from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { authedFetch, createStatusNote, renderStatus, showVerifyGate } from '@studio/verify-email';

const dashboard = document.querySelector('[data-studio-dashboard]');

// Guard: needs the dashboard (with backend URL) and firebase-auth.js inited.
if (dashboard && getApps().length) {
  const BACKEND = (dashboard.getAttribute('data-backend-url') || '').replace(/\/$/, '');
  const auth = getAuth(getApp());

  async function apiGet(path) {
    // Token, Authorization header and the retry-once on the email_verified
    // gate: authedFetch (studio-verify-email.js).
    const res = await authedFetch(BACKEND + path, { method: 'GET' });
    if (!res.ok) {
      const e = new Error('Request failed (' + res.status + ')');
      e.status = res.status;
      throw e;
    }
    return res.status === 204 ? null : res.json();
  }

  function addDownloadButton(action, orderId) {
    if (action.querySelector('[data-studio-download]')) return; // idempotent
    // Hide the "Details ›" fallback link once a real download exists.
    const details = action.querySelector('[data-order-details]');
    if (details) details.hidden = true;

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ed-studio-btn ed-studio-btn--sm';
    btn.setAttribute('data-studio-download', '');
    const idle = 'Download photo';
    btn.textContent = idle;

    const note = createStatusNote('data-download-note', 'ed-studio-download-note');

    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.textContent = 'Preparing…';
      try {
        const data = await apiGet('/job-status/download?orderId=' + encodeURIComponent(orderId));
        if (data && data.url) window.open(data.url, '_blank', 'noopener');
        btn.textContent = idle;
        btn.disabled = false;
      } catch (e) {
        // uGqPRiwp: a 403 here is the backend's email_verified gate. Relabelling
        // the button was a dead end — the customer had nowhere to verify. Render
        // the gate in the sibling status line (with the resend control) and
        // give the button back; any other failure clears the line and relabels.
        if (e.status === 403) {
          showVerifyGate(note, 'downloads');
          btn.textContent = idle;
          btn.disabled = false;
        } else {
          renderStatus(note, '', false, e.status);
          btn.textContent = 'Download failed — retry';
          setTimeout(() => { btn.textContent = idle; btn.disabled = false; }, 4000);
        }
      }
    });

    action.appendChild(btn);
    action.appendChild(note);
  }

  async function checkOrder(row) {
    const orderId = row.getAttribute('data-order-id');
    const action = row.querySelector('[data-order-action]');
    if (!orderId || !action) return;
    try {
      const job = await apiGet('/job-status?orderId=' + encodeURIComponent(orderId));
      if (job && job.status === 'delivered' && job.outputUrl) {
        addDownloadButton(action, orderId);
      }
    } catch (e) {
      // ⚠️ This catch used to swallow everything, INCLUDING the email_verified
      // 403 — and `addDownloadButton` is only called on the success path above,
      // so the resend control it mounts was UNREACHABLE by exactly the customers
      // it was built for. The fix and the bug were in the same file.
      if (e.status === 403) {
        let note = action.querySelector('[data-order-note]');
        if (!note) {
          note = createStatusNote('data-order-note', 'ed-studio-download-note');
          action.appendChild(note);
        }
        showVerifyGate(note, 'downloads');
        return;
      }
      // not-signed-in / transient → leave the Liquid fallback link as-is.
      // GUARD-OK: no user-visible failure — the server-rendered "Details ›" link
      // is still there and still correct; a toast here would be noise on every
      // signed-out page load.
      console.debug('[studio-delivery] job status unavailable:', e && e.message);
    }
  }

  // Re-scan on every auth transition (rows are server-rendered by Liquid).
  onAuthStateChanged(auth, (user) => {
    if (!user) return;
    document
      .querySelectorAll('.ed-studio-order-row[data-order-id]')
      .forEach(checkOrder);
  });
}
